import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import Button from '../../ui/Button';
import Input from '../../ui/Input';
import Modal, { FormModal } from '../../ui/Modal';
import Card, { CardHeader, InfoCard } from '../../ui/Card';
import {
  ArrowLeftIcon,
  PlusIcon,
  GlobeAltIcon,
  TrashIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  DocumentTextIcon,
  PuzzlePieceIcon,
  PaintBrushIcon,
  HeartIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline';

const WordPressManagement = ({ showNotification }) => {
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSiteModal, setShowAddSiteModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [testingConnection, setTestingConnection] = useState(null);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [testingNewSite, setTestingNewSite] = useState(false);

  // Add Site Form
  const [newSite, setNewSite] = useState({
    siteUrl: '',
    siteName: '',
    authMethod: 'application_password',
    credentials: {
      username: '',
      password: '',
      jwtToken: '',
      jwtRefreshToken: '',
      clientId: '',
      clientSecret: '',
      accessToken: '',
      refreshToken: '',
      scope: '',
      apiKey: ''
    }
  });

  useEffect(() => {
    fetchSites();
  }, []);

  const fetchSites = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/wordpress/sites');
      if (response.data.success) {
        setSites(response.data.data || []);
      }
    } catch (err) {
      showNotification?.('Failed to fetch WordPress sites: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const buildCredentialsPayload = () => {
    const payload = {};

    switch (newSite.authMethod) {
      case 'application_password':
      case 'basic_auth':
        payload.username = newSite.credentials.username;
        payload.password = newSite.credentials.password;
        break;

      case 'jwt':
        payload.jwtToken = newSite.credentials.jwtToken;
        payload.jwtRefreshToken = newSite.credentials.jwtRefreshToken;
        break;

      case 'oauth2':
        payload.clientId = newSite.credentials.clientId;
        payload.clientSecret = newSite.credentials.clientSecret;
        payload.accessToken = newSite.credentials.accessToken;
        payload.refreshToken = newSite.credentials.refreshToken;
        payload.scope = newSite.credentials.scope ? newSite.credentials.scope.split(',').map(s => s.trim()) : [];
        break;

      case 'api_key':
        payload.apiKey = newSite.credentials.apiKey;
        break;
    }

    return payload;
  };

  const handleTestConnection = async () => {
    try {
      setTestingNewSite(true);
      setConnectionTestResult(null);

      const payload = {
        siteUrl: newSite.siteUrl,
        authMethod: newSite.authMethod,
        credentials: buildCredentialsPayload()
      };

      const response = await axiosInstance.post('/api/wordpress/sites/test-connection', payload);

      if (response.data.success) {
        setConnectionTestResult({
          success: true,
          message: 'Connection successful! WordPress site is accessible.',
          details: response.data.details
        });
        showNotification?.('Connection test successful!', 'success');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message;
      setConnectionTestResult({
        success: false,
        message: errorMessage,
        troubleshooting: err.response?.data?.troubleshooting,
        details: err.response?.data?.details
      });
      showNotification?.('Connection test failed: ' + errorMessage, 'error');
    } finally {
      setTestingNewSite(false);
    }
  };

  const handleAddSite = async () => {
    // Require successful connection test before adding
    if (!connectionTestResult?.success) {
      showNotification?.('Please test the connection first to verify credentials', 'warning');
      return;
    }

    try {
      setLoading(true);

      const payload = {
        siteUrl: newSite.siteUrl,
        siteName: newSite.siteName,
        authMethod: newSite.authMethod,
        credentials: buildCredentialsPayload()
      };

      const response = await axiosInstance.post('/api/wordpress/sites', payload);

      if (response.data.success) {
        showNotification?.('WordPress site added successfully!', 'success');
        setShowAddSiteModal(false);
        resetAddSiteForm();
        setConnectionTestResult(null);
        fetchSites();
      }
    } catch (err) {
      showNotification?.('Failed to add site: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleTestSite = async (siteId) => {
    try {
      setTestingConnection(siteId);
      const response = await axiosInstance.post(`/api/wordpress/sites/${siteId}/test`);

      if (response.data.success) {
        showNotification?.('Site connection test successful!', 'success');
        fetchSites();
      }
    } catch (err) {
      showNotification?.('Connection test failed: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setTestingConnection(null);
    }
  };

  const handleDeleteSite = async (siteId) => {
    if (!window.confirm('Are you sure you want to remove this WordPress site?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.delete(`/api/wordpress/sites/${siteId}`);

      if (response.data.success) {
        showNotification?.('WordPress site removed successfully!', 'success');
        fetchSites();
        if (selectedSite?._id === siteId) {
          setSelectedSite(null);
        }
      }
    } catch (err) {
      showNotification?.('Failed to remove site: ' + (err.response?.data?.message || err.message), 'error');
    } finally {
      setLoading(false);
    }
  };

  const resetAddSiteForm = () => {
    setNewSite({
      siteUrl: '',
      siteName: '',
      authMethod: 'application_password',
      credentials: {
        username: '',
        password: '',
        jwtToken: '',
        jwtRefreshToken: '',
        clientId: '',
        clientSecret: '',
        accessToken: '',
        refreshToken: '',
        scope: '',
        apiKey: ''
      }
    });
    setConnectionTestResult(null);
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      testing: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      inactive: 'bg-gray-100 text-gray-800 dark:bg-[#111111]/30 dark:text-gray-400'
    };
    return variants[status] || variants.inactive;
  };

  const getHealthBadge = (status) => {
    const variants = {
      healthy: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      warning: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      unknown: 'bg-gray-100 text-gray-800 dark:bg-[#111111]/30 dark:text-gray-400'
    };
    return variants[status] || variants.unknown;
  };

  const renderCredentialFields = () => {
    const { authMethod, credentials } = newSite;

    switch (authMethod) {
      case 'application_password':
      case 'basic_auth':
        return (
          <div className="space-y-4">
            <Input
              label="WordPress Username"
              value={credentials.username}
              onChange={(e) => setNewSite({
                ...newSite,
                credentials: { ...credentials, username: e.target.value }
              })}
              required
              placeholder="your-username"
            />
            <Input
              label={authMethod === 'application_password' ? 'Application Password' : 'Password'}
              type="password"
              value={credentials.password}
              onChange={(e) => setNewSite({
                ...newSite,
                credentials: { ...credentials, password: e.target.value }
              })}
              required
              helperText={authMethod === 'application_password'
                ? 'Generate this in WordPress Admin → Users → Profile → Application Passwords'
                : 'Your WordPress admin password (requires HTTPS)'}
            />
          </div>
        );

      case 'jwt':
        return (
          <div className="space-y-4">
            <Input
              label="JWT Token"
              value={credentials.jwtToken}
              onChange={(e) => setNewSite({
                ...newSite,
                credentials: { ...credentials, jwtToken: e.target.value }
              })}
              required
              helperText="Requires JWT Authentication plugin"
            />
            <Input
              label="JWT Refresh Token (Optional)"
              value={credentials.jwtRefreshToken}
              onChange={(e) => setNewSite({
                ...newSite,
                credentials: { ...credentials, jwtRefreshToken: e.target.value }
              })}
            />
          </div>
        );

      case 'oauth2':
        return (
          <div className="space-y-4">
            <Input
              label="Client ID"
              value={credentials.clientId}
              onChange={(e) => setNewSite({
                ...newSite,
                credentials: { ...credentials, clientId: e.target.value }
              })}
              required
            />
            <Input
              label="Client Secret"
              type="password"
              value={credentials.clientSecret}
              onChange={(e) => setNewSite({
                ...newSite,
                credentials: { ...credentials, clientSecret: e.target.value }
              })}
              required
            />
            <Input
              label="Access Token"
              value={credentials.accessToken}
              onChange={(e) => setNewSite({
                ...newSite,
                credentials: { ...credentials, accessToken: e.target.value }
              })}
              required
            />
            <Input
              label="Refresh Token (Optional)"
              value={credentials.refreshToken}
              onChange={(e) => setNewSite({
                ...newSite,
                credentials: { ...credentials, refreshToken: e.target.value }
              })}
            />
            <Input
              label="Scope (comma-separated)"
              value={credentials.scope}
              onChange={(e) => setNewSite({
                ...newSite,
                credentials: { ...credentials, scope: e.target.value }
              })}
              helperText="e.g., read, write, delete"
            />
          </div>
        );

      case 'api_key':
        return (
          <Input
            label="API Key"
            type="password"
            value={credentials.apiKey}
            onChange={(e) => setNewSite({
              ...newSite,
              credentials: { ...credentials, apiKey: e.target.value }
            })}
            required
            helperText="Custom API key from your WordPress plugin"
          />
        );

      default:
        return null;
    }
  };

  // Site Detail View
  if (selectedSite) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setSelectedSite(null)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-lg transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {selectedSite.siteName || selectedSite.siteUrl}
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {selectedSite.siteUrl}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(selectedSite.status)}`}>
            {selectedSite.status}
          </span>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-800 mb-6">
          <nav className="flex space-x-8">
            {['Overview', 'Health', 'AI Actions', 'Settings'].map((tab, index) => (
              <button
                key={tab}
                onClick={() => setActiveTab(index)}
                className={`
                  pb-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === index
                    ? 'border-orange-500 text-orange-600 dark:text-orange-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <DocumentTextIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Content</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Posts:</span>
                  <span className="font-medium">{selectedSite.health?.postCount || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span>Pages:</span>
                  <span className="font-medium">{selectedSite.health?.pageCount || 0}</span>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                  <PuzzlePieceIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Plugins</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Installed:</span>
                  <span className="font-medium">{selectedSite.health?.pluginCount || 0}</span>
                </div>
              </div>
            </Card>

            <Card>
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-2 bg-pink-100 dark:bg-pink-900/30 rounded-lg">
                  <PaintBrushIcon className="w-6 h-6 text-pink-600 dark:text-pink-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Themes</h3>
              </div>
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex justify-between">
                  <span>Installed:</span>
                  <span className="font-medium">{selectedSite.health?.themeCount || 0}</span>
                </div>
              </div>
            </Card>
          </div>
        )}

        {activeTab === 1 && (
          <Card>
            <CardHeader title="Site Health" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Status</label>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${getHealthBadge(selectedSite.health?.status)}`}>
                  {selectedSite.health?.status || 'unknown'}
                </span>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">WordPress Version</label>
                <p className="mt-2 text-gray-900 dark:text-gray-100">{selectedSite.health?.wordpressVersion || 'Unknown'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">PHP Version</label>
                <p className="mt-2 text-gray-900 dark:text-gray-100">{selectedSite.health?.phpVersion || 'Unknown'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Last Check</label>
                <p className="mt-2 text-gray-900 dark:text-gray-100">
                  {selectedSite.health?.lastCheck
                    ? new Date(selectedSite.health.lastCheck).toLocaleString()
                    : 'Never'}
                </p>
              </div>
            </div>

            {selectedSite.health?.issues && selectedSite.health.issues.length > 0 && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">Issues</h4>
                <div className="space-y-3">
                  {selectedSite.health.issues.map((issue, idx) => (
                    <div key={idx} className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm font-medium text-red-900 dark:text-red-300">{issue.message}</p>
                      <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                        Severity: {issue.severity} • {new Date(issue.detectedAt).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}

        {activeTab === 2 && (
          <Card>
            <CardHeader title="AI Actions History" />
            {selectedSite.aiActions && selectedSite.aiActions.length > 0 ? (
              <div className="space-y-3">
                {selectedSite.aiActions.slice().reverse().map((action, idx) => (
                  <div
                    key={idx}
                    className="p-4 bg-gray-50 dark:bg-[#1a1a1a]/50 border border-gray-200 dark:border-gray-800 rounded-lg"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">{action.action}</h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Type: {action.actionType} • {new Date(action.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <span className={`
                        px-2 py-1 rounded-full text-xs font-medium
                        ${action.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'}
                      `}>
                        {action.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 dark:text-gray-400 text-center py-8">No AI actions recorded yet</p>
            )}
          </Card>
        )}

        {activeTab === 3 && (
          <Card>
            <CardHeader title="Site Settings" />
            <div className="space-y-6">
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Site URL</label>
                <p className="mt-2 text-gray-900 dark:text-gray-100">{selectedSite.siteUrl}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Authentication Method</label>
                <p className="mt-2 text-gray-900 dark:text-gray-100 capitalize">{selectedSite.authMethod.replace(/_/g, ' ')}</p>
              </div>
              <div className="pt-6 border-t border-gray-200 dark:border-gray-800">
                <Button
                  variant="danger"
                  icon={<TrashIcon />}
                  onClick={() => {
                    setSelectedSite(null);
                    handleDeleteSite(selectedSite._id);
                  }}
                >
                  Remove Site
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>
    );
  }

  // Main Sites List View
  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            WordPress Site Management
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage your WordPress sites and let AI handle development tasks
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="secondary"
            icon={<ArrowPathIcon />}
            onClick={fetchSites}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            icon={<PlusIcon />}
            onClick={() => setShowAddSiteModal(true)}
          >
            Add Site
          </Button>
        </div>
      </div>

      {/* Sites List */}
      {loading && sites.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
        </div>
      ) : sites.length === 0 ? (
        <Card className="text-center py-12">
          <div className="flex justify-center mb-4">
            <div className="p-4 bg-gray-100 dark:bg-[#1a1a1a] rounded-full">
              <GlobeAltIcon className="w-12 h-12 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No WordPress Sites Connected
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-6">
            Add your first WordPress site to get started with AI-powered management
          </p>
          <Button
            variant="primary"
            icon={<PlusIcon />}
            onClick={() => setShowAddSiteModal(true)}
          >
            Add Site
          </Button>
        </Card>
      ) : (
        <div className="bg-white dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-800">
            <thead className="bg-gray-50 dark:bg-[#111111]/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Site Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  URL
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Auth Method
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Health
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Stats
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-[#1a1a1a] divide-y divide-gray-200 dark:divide-gray-800">
              {sites.map((site) => (
                <tr key={site._id} className="hover:bg-gray-50 dark:hover:bg-[#222222]/50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {site.siteName || 'Unnamed Site'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <a
                      href={site.siteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {site.siteUrl}
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-800 dark:bg-[#222222] dark:text-gray-300">
                      {site.authMethod}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusBadge(site.status)}`}>
                      {site.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getHealthBadge(site.health?.status)}`}>
                      {site.health?.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs text-gray-600 dark:text-gray-400">
                      <div>Posts: {site.health?.postCount || 0}</div>
                      <div>Plugins: {site.health?.pluginCount || 0}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => setSelectedSite(site)}
                        className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <Cog6ToothIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleTestSite(site._id)}
                        disabled={testingConnection === site._id}
                        className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors disabled:opacity-50"
                        title="Test Connection"
                      >
                        {testingConnection === site._id ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                        ) : (
                          <ArrowPathIcon className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleDeleteSite(site._id)}
                        className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        title="Delete Site"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Site Modal */}
      <Modal
        isOpen={showAddSiteModal}
        onClose={() => {
          setShowAddSiteModal(false);
          resetAddSiteForm();
        }}
        title="Add WordPress Site"
        size="lg"
        footer={
          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={() => {
                setShowAddSiteModal(false);
                resetAddSiteForm();
              }}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleAddSite}
              disabled={loading || !connectionTestResult?.success}
              loading={loading}
            >
              Add Site
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          <Input
            label="Site URL"
            value={newSite.siteUrl}
            onChange={(e) => {
              setNewSite({ ...newSite, siteUrl: e.target.value });
              setConnectionTestResult(null); // Clear test result when URL changes
            }}
            required
            placeholder="https://yoursite.com"
            helperText="Full URL including https://"
          />

          <Input
            label="Site Name (Optional)"
            value={newSite.siteName}
            onChange={(e) => setNewSite({ ...newSite, siteName: e.target.value })}
            placeholder="My WordPress Site"
          />

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              Authentication Method <span className="text-red-500">*</span>
            </label>
            <select
              value={newSite.authMethod}
              onChange={(e) => {
                setNewSite({
                  ...newSite,
                  authMethod: e.target.value,
                  credentials: {
                    username: '',
                    password: '',
                    jwtToken: '',
                    jwtRefreshToken: '',
                    clientId: '',
                    clientSecret: '',
                    accessToken: '',
                    refreshToken: '',
                    scope: '',
                    apiKey: ''
                  }
                });
                setConnectionTestResult(null); // Clear test result when auth method changes
              }}
              className="w-full px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            >
              <option value="application_password">Application Password (Recommended)</option>
              <option value="jwt">JWT Authentication</option>
              <option value="oauth2">OAuth 2.0</option>
              <option value="basic_auth">Basic Authentication</option>
              <option value="api_key">API Key</option>
            </select>
          </div>

          <div className="pt-4">
            {renderCredentialFields()}
          </div>

          {/* Test Connection Button */}
          <div className="pt-4">
            <Button
              variant="secondary"
              icon={<ArrowPathIcon />}
              onClick={handleTestConnection}
              disabled={testingNewSite || !newSite.siteUrl || loading}
              loading={testingNewSite}
              fullWidth
            >
              {testingNewSite ? 'Testing Connection...' : 'Test Connection'}
            </Button>
          </div>

          {/* Connection Test Result */}
          {connectionTestResult && (
            <div className="space-y-2">
              <InfoCard
                variant={connectionTestResult.success ? 'success' : 'error'}
                title={connectionTestResult.success ? 'Connection Successful' : 'Connection Failed'}
                description={connectionTestResult.message}
                icon={connectionTestResult.success ? <CheckCircleIcon /> : <ExclamationCircleIcon />}
              />

              {/* Troubleshooting Steps for Failed Connection */}
              {!connectionTestResult.success && connectionTestResult.troubleshooting && connectionTestResult.troubleshooting.length > 0 && (
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <div className="flex items-start space-x-2 mb-3">
                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-300">Troubleshooting Steps:</p>
                  </div>
                  <ul className="space-y-2 ml-7">
                    {connectionTestResult.troubleshooting.map((step, index) => (
                      <li key={index} className="text-xs text-amber-800 dark:text-amber-400 flex items-start">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-600 dark:bg-amber-400 mt-1.5 mr-2 flex-shrink-0"></span>
                        <span>{step}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Site Information for Successful Connection */}
              {connectionTestResult.success && connectionTestResult.details && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-xs font-medium text-blue-900 dark:text-blue-300 mb-2">Site Information</p>
                  <div className="grid grid-cols-2 gap-2 text-xs text-blue-800 dark:text-blue-400">
                    {connectionTestResult.details.siteName && (
                      <div>
                        <span className="font-medium">Site:</span> {connectionTestResult.details.siteName}
                      </div>
                    )}
                    {connectionTestResult.details.wordpressVersion && (
                      <div>
                        <span className="font-medium">WordPress:</span> {connectionTestResult.details.wordpressVersion}
                      </div>
                    )}
                    {connectionTestResult.details.phpVersion && (
                      <div>
                        <span className="font-medium">PHP:</span> {connectionTestResult.details.phpVersion}
                      </div>
                    )}
                    {connectionTestResult.details.pluginCount !== undefined && (
                      <div>
                        <span className="font-medium">Plugins:</span> {connectionTestResult.details.pluginCount}
                      </div>
                    )}
                    {connectionTestResult.details.themeCount !== undefined && (
                      <div>
                        <span className="font-medium">Themes:</span> {connectionTestResult.details.themeCount}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <InfoCard
            variant="orange"
            title="Recommended"
            description="Application Password is the most secure method built into WordPress 5.6+. Generate one in your WordPress Admin under Users → Profile → Application Passwords."
            icon={<CheckCircleIcon />}
          />
        </div>
      </Modal>
    </div>
  );
};

export default WordPressManagement;
